import { useState } from 'react';
import { Alert, App, Button, Input, InputNumber, Modal, Space, Typography } from 'antd';
import { StopOutlined, UnlockOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { errMsg } from '../../lib/errors';
import { Can } from '../../rbac/RequirePermission';

/**
 * Suspend / lift suspension, on the listener's own page — so an admin reading
 * a listener's calls and earnings can act without going back to the list and
 * finding the row again. Both write an audited reason (≥ 10 chars), the same
 * rule every other listener status change follows. Permission: listeners.suspend.
 */
export function ListenerSuspensionControl({
  listenerId,
  listenerName,
  isSuspended,
}: {
  listenerId: string;
  listenerName: string;
  isSuspended: boolean;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [days, setDays] = useState(7);

  const close = () => {
    setOpen(false);
    setReason('');
    setDays(7);
  };

  const act = useMutation({
    mutationFn: async () =>
      isSuspended
        ? api.post(`/admin/listeners/${listenerId}/unsuspend`, { reason: reason.trim() })
        : api.post(`/admin/listeners/${listenerId}/suspend`, { reason: reason.trim(), durationDays: days }),
    onSuccess: () => {
      message.success(isSuspended ? 'Suspension lifted' : `Suspended for ${days} day${days === 1 ? '' : 's'}`);
      close();
      void qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'listener-overview', listenerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
    onError: (e: unknown) =>
      message.error(errMsg(e, isSuspended ? 'Could not lift the suspension' : 'Could not suspend')),
  });

  const valid = reason.trim().length >= 10 && (isSuspended || (days >= 1 && days <= 365));

  return (
    <Can permission="listeners.suspend">
      <Button
        size="small"
        danger={!isSuspended}
        icon={isSuspended ? <UnlockOutlined /> : <StopOutlined />}
        onClick={() => setOpen(true)}
      >
        {isSuspended ? 'Lift suspension' : 'Suspend'}
      </Button>
      <Modal
        title={isSuspended ? `Lift the suspension on ${listenerName}?` : `Suspend ${listenerName}?`}
        open={open}
        onCancel={close}
        onOk={() => act.mutate()}
        okText={isSuspended ? 'Lift suspension' : 'Suspend'}
        okButtonProps={{ danger: !isSuspended, disabled: !valid, loading: act.isPending }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type={isSuspended ? 'info' : 'warning'}
            showIcon
            message={
              isSuspended
                ? 'The listener becomes callable again and reappears to callers the next time they go online.'
                : 'The listener drops out of discovery within seconds, goes offline, and cannot receive calls until the suspension ends or is lifted.'
            }
          />
          {!isSuspended && (
            <div>
              <Typography.Text>Suspend for</Typography.Text>{' '}
              <InputNumber min={1} max={365} value={days} onChange={(v) => setDays(v ?? 1)} addonAfter="days" />
            </div>
          )}
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (≥ 10 chars) — written to the audit log"
            maxLength={500}
          />
        </Space>
      </Modal>
    </Can>
  );
}
